import router from 'next/router';
import { useCallback, useMemo } from 'react';
import styles from './AssistantModal.module.scss';
import { CommonBasicButton } from '~/components/common/button/CommonBasicButton';
import { CommonImage } from '~/components/common/image/CommonImage';
import { CommonUnderCloseModal } from '~/components/common/modal/CommonUnderCloseModal';
import { BasicParagraph } from '~/components/common/text/BasicParagraph';
import { BasicSpan } from '~/components/common/text/BasicSpan';
import type { AssistantEventId } from '~/features/assistant/hooks/useAssistant';
import { useAssistantInterval } from '~/features/assistant/hooks/useAssistantInterval';
import { useTranslation } from '~/hooks/useTranslation';
import { isLocalizeKey } from '~/interfaces/i18n';
import { colors } from '~/styles/ts/colors';
import { path } from '~/utils/path';

interface Props {
  eventId: AssistantEventId;
  destination?: string;
  imageUrl?: string;
  message: string;
  need_recommended: boolean;
}

export const AssistantModal = ({ eventId, destination, imageUrl, message }: Props) => {
  const { t } = useTranslation();
  const { setAssistantInterval } = useAssistantInterval();

  const closeAssistantModal = useCallback(() => {
    setAssistantInterval(eventId);
  }, [setAssistantInterval, eventId]);

  const onClickButton = useCallback(async () => {
    if (!destination) return;
    setAssistantInterval(eventId);
    if (eventId === '16_birthday') location.href = destination;
    if (eventId === 'grown_up') await router.push(destination);
  }, [setAssistantInterval, eventId, destination]);

  const buttonText = useMemo(() => {
    if (eventId === '16_birthday') return 'assistant_button_one_asics';
    if (eventId === 'grown_up') return 'assistant_button_growth_graph';
  }, [eventId]);

  const translateMessage = useMemo(() => {
    if (!isLocalizeKey(message)) return message;
    return t(message);
  }, [message, t]);

  return (
    <CommonUnderCloseModal size="672-1008" onClick={closeAssistantModal}>
      <div className={styles.container}>
        <div className={styles.header}>
          <CommonImage src={path.image('/assistant/header-left.png')} width={118} height={105} />
          <BasicSpan
            size={24}
            fontFamily="sen"
            colorStyle="inversed"
            letterSpacing="0.03em"
            lineHeight={1}
            weight={700}
            className={styles['header-text']}
          >
            ASICS ASSISTANT
          </BasicSpan>
        </div>
        {imageUrl && <CommonImage src={imageUrl} width={672} height={464} />}

        <BasicParagraph
          size={28}
          color={colors.asicsKidsNavy}
          lineHeight={2}
          weight={700}
          letterSpacing="0.03em"
          className={styles['message']}
        >
          {translateMessage}
        </BasicParagraph>
        {buttonText && (
          <div className={styles.buttonWrapper}>
            <CommonBasicButton
              size={'400-112'}
              text={buttonText}
              colorType={'commonPositive'}
              onClick={onClickButton}
            />
          </div>
        )}
      </div>
    </CommonUnderCloseModal>
  );
};
